import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { MessageCircle, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { ReportButton } from "@/components/community/ReportButton";
import { mySongCommentIds } from "@/lib/my-comments.functions";


interface CommentRow {
  id: string;
  body: string;
  created_at: string;
  author_display_name: string | null;
  is_mine?: boolean;
}

export function SongComments({ releaseSlug, songSlug }: { releaseSlug: string; songSlug: string }) {
  const [rows, setRows] = useState<CommentRow[]>([]);
  const [me, setMe] = useState<string | null>(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function load() {
    const { data } = await supabase
      .from("song_comments")
      .select("id, body, created_at, author_display_name")
      .eq("release_slug", releaseSlug)
      .eq("song_slug", songSlug)
      .is("hidden_at" as any, null)
      .order("created_at", { ascending: false })
      .limit(200);


    const list = (data ?? []) as CommentRow[];

    // Separately fetch the current user's own comment ids so we can render
    // the delete button without exposing user_id to anonymous readers.
    const { data: authData } = await supabase.auth.getUser();
    const uid = authData.user?.id ?? null;
    if (uid) {
      try {
        const mine = await mySongCommentIds({ data: { releaseSlug, songSlug } });
        const mineIds = new Set(mine);
        list.forEach((r) => { r.is_mine = mineIds.has(r.id); });
      } catch {
        /* owner controls simply stay hidden if the lookup fails */
      }
    }

    setRows(list);
  }

  useEffect(() => {
    void load();
    void supabase.auth.getUser().then(({ data }) => setMe(data.user?.id ?? null));
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_IN" || event === "SIGNED_OUT" || event === "USER_UPDATED") {
        setMe(session?.user?.id ?? null);
      }
    });
    return () => sub.subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [releaseSlug, songSlug]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!me || !text.trim() || busy) return;
    setBusy(true); setErr(null);
    const body = text.trim().slice(0, 2000);
    const { error } = await supabase.from("song_comments").insert({
      user_id: me, release_slug: releaseSlug, song_slug: songSlug, body,
    });
    setBusy(false);
    if (error) { setErr(error.message); return; }
    setText("");
    void load();
  }

  async function remove(id: string) {
    if (!confirm("Delete this comment?")) return;
    const { error } = await supabase.from("song_comments").delete().eq("id", id);
    if (error) return alert(error.message);
    setRows((prev) => prev.filter((r) => r.id !== id));
  }

  return (
    <section aria-labelledby="song-comments-heading">
      <div className="mb-6 flex items-center gap-3">
        <MessageCircle size={16} className="text-gold" aria-hidden />
        <h2 id="song-comments-heading" className="font-serif text-3xl">Reflections</h2>
        <span className="text-xs text-muted-foreground">{rows.length}</span>
      </div>

      {me ? (
        <form onSubmit={submit} className="mb-8 rounded-2xl border hairline bg-obsidian/40 p-4">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={3}
            maxLength={2000}
            placeholder="Leave a reflection — what did this song open in you?"
            className="w-full resize-none bg-transparent text-sm text-ivory placeholder:text-muted-foreground focus:outline-none"
          />
          {err && <p className="mt-2 text-xs text-red-400">{err}</p>}
          <div className="mt-3 flex items-center justify-between border-t hairline pt-3">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{text.length}/2000</span>
            <button
              type="submit"
              disabled={busy || !text.trim()}
              className="rounded-full bg-gold-gradient px-5 py-2 text-xs font-semibold uppercase tracking-widest text-obsidian shadow-gold disabled:opacity-40"
            >
              {busy ? "Posting…" : "Post"}
            </button>
          </div>
        </form>
      ) : (
        <div className="mb-8 rounded-2xl border hairline bg-obsidian/40 p-6 text-center">
          <p className="text-sm text-muted-foreground">
            <Link to="/auth" className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold">Sign in</Link> to leave a reflection.
          </p>
        </div>
      )}

      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">Be the first to reflect on this transmission.</p>
      ) : (
        <ul className="space-y-4">
          {rows.map((c) => (
            <li key={c.id} className="rounded-xl border hairline bg-obsidian/30 p-4">
              <div className="flex items-baseline justify-between gap-3">
                <p className="text-xs uppercase tracking-[0.25em] text-gold">
                  {c.author_display_name ?? "Anonymous"}
                </p>
                <div className="flex items-center gap-3">
                  <time className="text-[10px] text-muted-foreground" dateTime={c.created_at}>
                    {new Date(c.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                  </time>
                  {me && c.is_mine ? (
                    <button
                      onClick={() => remove(c.id)}
                      className="text-muted-foreground hover:text-red-400"
                      aria-label="Delete comment"
                    >
                      <Trash2 size={12} />
                    </button>
                  ) : me ? (
                    <ReportButton targetKind="song_comment" targetId={c.id} />
                  ) : null}
                </div>

              </div>
              <p className="mt-2 whitespace-pre-wrap text-sm text-ivory/85">{c.body}</p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
